import { cn } from 'components/UI/cn'

/**
 * Gmail-style shell for the inline reply composer: user avatar on the left,
 * a rounded card with border + shadow on the right. Caps height with a scroll
 * region so long drafts stay within a reasonable footprint.
 *
 * Shared between the company email inbox (`ComposePanel` in inline mode) and
 * the campaign reply inbox (`ReplyComposePanel`) so the two experiences look
 * and behave identically.
 */
export function InlineComposeShell({
  userInitials,
  maxHeightClass = 'max-h-[50vh]',
  flat = false,
  children,
}: {
  /** 1–2 character initials shown in the avatar bubble. */
  userInitials: string
  /**
   * Tailwind max-height class for the card. Defaults to `max-h-[50vh]`.
   * Ignored when `flat` is true.
   */
  maxHeightClass?: string
  /**
   * When true, drop the card's own height cap and internal scroll. Use when
   * the panel is embedded in a parent that already owns scrolling — e.g.
   * the campaign-replies inbox uses a single scroll surface wrapping
   * thread + composer (FIR-7419) so the user can scroll back to the
   * original message while the composer is open.
   */
  flat?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="border-t border-line-subtle px-6 py-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0 mt-1">
          <span className="text-neutral-600 font-semibold text-sm">
            {userInitials}
          </span>
        </div>
        <div
          className={cn(
            'flex-1 rounded-xl border border-line-subtle bg-surface flex flex-col',
            'shadow-[0px_2px_5px_rgba(0,0,0,0.10),0px_1px_2px_rgba(0,0,0,0.16)]',
            !flat && maxHeightClass
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
